import clsx from 'clsx'
import {
  FC,
  KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {createPortal} from 'react-dom'
import {Calendar, DateObject} from 'react-multi-date-picker'
import TimePicker from 'react-multi-date-picker/plugins/time_picker'
import persian from 'react-date-object/calendars/persian'
import persian_fa from 'react-date-object/locales/persian_fa'
import {hasLunarHolidays, holidayNamesOn, holidaysInMonth} from './persianHolidays'
import {noteDialogEdited} from '../DialogEscapeKey'
import {isPOS} from '../../crud-helper/helpers'
import {
  FRIDAY_INDEX,
  PERSIAN_MONTHS,
  PersianDateParts,
  continueTypedPersianDate,
  describeGregorianDay,
  describePersianDay,
  formatPersianDateValue,
  gregorianSpanOfPersianMonth,
  gregorianSpanOfPersianYear,
  parsePersianDateValue,
  parseTypedPersianDate,
  persianDayKey,
  persianMonthLength,
  tehranNow,
  toGregorian,
  toPersianDateObject,
  toPersianDigits,
} from './persianDateValue'
import './PersianDatePicker.css'

export type PersianDatePickerProps = {
  id?: string
  /** `YYYY/MM/DD`, or `YYYY/MM/DD HH:mm` with `timePicker`; '' when empty. */
  value?: unknown
  onChange: (value: string) => void
  /** The earliest day that can be chosen; a time it carries is ignored. */
  minDate?: string
  /** The latest day that can be chosen; a time it carries is ignored. */
  maxDate?: string
  timePicker?: boolean
  readOnly?: boolean
  disabled?: boolean
  /** Whether the field may be left empty; only then can it be cleared. */
  clearable?: boolean
  /** Never a sample day: customers took a pale «۱۴۰۵/۰۷/۰۱» for a date already entered. */
  placeholder?: string
  /**
   * Whether a tap on the field opens the calendar. Off on card-reader (POS) hosts: there the
   * field is typed into, separators added as the day is typed, and only the calendar button
   * opens the calendar.
   */
  openOnFieldClick?: boolean
  className?: string
}

type DayRange = {min: PersianDateParts | null; max: PersianDateParts | null}
type View = {year: number; month: number}
type Panel = 'days' | 'months' | 'years'

const POPOVER_WIDTH = 336
const SHEET_QUERY = '(max-width: 575.98px)'
const COARSE_POINTER_QUERY = '(pointer: coarse)'
const WEEK_DAY_LABELS = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج']
// The Calendar reads `role` (its typings omit it) and defaults to "dialog", which would nest a
// second, unnamed dialog inside ours.
const CALENDAR_GRID_ROLE = {role: 'group'}

const mediaMatches = (query: string) =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(query).matches

const monthIndex = (view: View) => view.year * 12 + (view.month - 1)
const viewOfIndex = (index: number): View => ({
  year: Math.floor(index / 12),
  month: (index % 12) + 1,
})

const clampView = (view: View, {min, max}: DayRange) => {
  let index = monthIndex(view)
  if (min) index = Math.max(index, monthIndex(min))
  if (max) index = Math.min(index, monthIndex(max))
  return viewOfIndex(index)
}

const isWithinRange = (day: PersianDateParts, {min, max}: DayRange) => {
  const key = persianDayKey(day)
  return (!min || key >= persianDayKey(min)) && (!max || key <= persianDayKey(max))
}

const describeRange = ({min, max}: DayRange) => {
  const day = (parts: PersianDateParts) => toPersianDigits(formatPersianDateValue(parts, false))
  if (min && max) return `تاریخ باید بین ${day(min)} و ${day(max)} باشد.`
  if (min) return `تاریخ نمی‌تواند پیش از ${day(min)} باشد.`
  if (max) return `تاریخ نمی‌تواند پس از ${day(max)} باشد.`
  return ''
}

const twoDigits = (value: number) => String(value).padStart(2, '0')

const CalendarIcon: FC = () => (
  <svg viewBox='0 0 24 24' width='18' height='18' aria-hidden='true' focusable='false'>
    <rect
      x='3.5'
      y='5'
      width='17'
      height='15.5'
      rx='3.5'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.6'
    />
    <path
      d='M3.5 9.75h17M8 3.25v3.5M16 3.25v3.5'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.6'
      strokeLinecap='round'
    />
    <circle cx='8.5' cy='13.75' r='1.05' fill='currentColor' />
    <circle cx='12' cy='13.75' r='1.05' fill='currentColor' />
    <circle cx='15.5' cy='13.75' r='1.05' fill='currentColor' />
    <circle cx='8.5' cy='17' r='1.05' fill='currentColor' />
    <circle cx='12' cy='17' r='1.05' fill='currentColor' />
  </svg>
)

const ClearIcon: FC = () => (
  <svg viewBox='0 0 24 24' width='16' height='16' aria-hidden='true' focusable='false'>
    <path
      d='M7.5 7.5l9 9M16.5 7.5l-9 9'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.8'
      strokeLinecap='round'
    />
  </svg>
)

/** In a right-to-left calendar «previous» sits on the right and points right. */
const ChevronIcon: FC<{direction: 'previous' | 'next'}> = ({direction}) => (
  <svg viewBox='0 0 24 24' width='18' height='18' aria-hidden='true' focusable='false'>
    <path
      d={direction === 'previous' ? 'M9.5 6l6 6-6 6' : 'M14.5 6l-6 6 6 6'}
      fill='none'
      stroke='currentColor'
      strokeWidth='1.8'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const CaretIcon: FC<{open: boolean}> = ({open}) => (
  <svg
    viewBox='0 0 24 24'
    width='14'
    height='14'
    aria-hidden='true'
    focusable='false'
    className={clsx('mp-dp-title__caret', {'is-open': open})}
  >
    <path
      d='M7 10l5 5 5-5'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.8'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

/**
 * The panel's Jalali date field: type a day, or pick it from a calendar that marks Fridays and
 * official holidays and shows each day's Gregorian date. On a phone the calendar opens as a
 * bottom sheet. The value is the string the panel has always stored (`YYYY/MM/DD`, plus
 * ` HH:mm` with `timePicker`), in Latin digits; '' means empty.
 */
export const PersianDatePicker: FC<PersianDatePickerProps> = ({
  id,
  value,
  onChange,
  minDate,
  maxDate,
  timePicker = false,
  readOnly = false,
  disabled = false,
  clearable = true,
  placeholder,
  openOnFieldClick = !isPOS,
  className,
}) => {
  const generatedId = useId().replace(/:/g, '')
  const inputId = id || `mp-dp-${generatedId}`
  const popoverId = `${inputId}-calendar`
  const errorId = `${inputId}-error`
  const rootRef = useRef<HTMLDivElement>(null)
  const fieldRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const focusGridOnOpen = useRef(false)

  const rawValue = value === null || value === undefined ? '' : String(value)
  const selected = useMemo(() => parsePersianDateValue(rawValue), [rawValue])
  const min = useMemo(() => parsePersianDateValue(minDate), [minDate])
  const max = useMemo(() => parsePersianDateValue(maxDate), [maxDate])
  const range = useMemo<DayRange>(() => ({min, max}), [min, max])
  const displayValue = selected
    ? formatPersianDateValue(selected, timePicker && selected.hasTime)
    : rawValue
  const hasValue = rawValue !== ''
  const locked = readOnly || disabled
  const canClear = clearable && hasValue && !locked

  const [open, setOpen] = useState(false)
  const [sheet, setSheet] = useState(false)
  const [coarsePointer] = useState(() => mediaMatches(COARSE_POINTER_QUERY))
  const [position, setPosition] = useState<{top: number; left: number; width: number}>()
  const [text, setText] = useState(displayValue)
  const [editing, setEditing] = useState(false)
  const [typedError, setTypedError] = useState('')
  const [view, setView] = useState<View>(() => clampView(selected || tehranNow(), range))
  const [calendarKey, setCalendarKey] = useState(0)
  const [panel, setPanel] = useState<Panel>('days')
  const [yearsStart, setYearsStart] = useState(0)

  useEffect(() => {
    if (!editing) setText(displayValue)
  }, [displayValue, editing])

  const now = tehranNow()
  const todayKey = persianDayKey(now)
  const todayAllowed = isWithinRange(now, range)

  const openCalendar = () => {
    if (locked || open) return
    setView(clampView(selected || now, range))
    setPanel('days')
    setCalendarKey((key) => key + 1)
    setSheet(mediaMatches(SHEET_QUERY))
    setPosition(undefined)
    setOpen(true)
  }

  const closeCalendar = useCallback((returnFocus: boolean) => {
    setOpen(false)
    setPanel('days')
    if (returnFocus) inputRef.current?.focus({preventScroll: true})
  }, [])

  const emit = (parts: PersianDateParts | null) => {
    const next = parts ? formatPersianDateValue(parts, timePicker) : ''
    setEditing(false)
    setTypedError('')
    setText(next)
    if (next !== rawValue) {
      noteDialogEdited(rootRef.current)
      onChange(next)
    }
  }

  const clear = (returnFocus: boolean) => {
    emit(null)
    if (open) closeCalendar(returnFocus)
    else if (returnFocus) inputRef.current?.focus()
  }

  const pickToday = () => {
    if (!todayAllowed) return
    emit(now)
    if (timePicker) {
      setView({year: now.year, month: now.month})
      setCalendarKey((key) => key + 1)
    } else {
      closeCalendar(!sheet)
    }
  }

  /** Applies what was typed; false while it is not a usable day. */
  const commitTyped = () => {
    if (!editing) return true
    const typed = text.trim()
    if (!typed) {
      if (clearable) {
        emit(null)
      } else {
        setEditing(false)
        setTypedError('')
        setText(displayValue)
      }
      return true
    }
    const parsed = parseTypedPersianDate(typed)
    if (!parsed) {
      setTypedError(
        timePicker
          ? 'تاریخ و ساعت را به شکل ۱۴۰۵/۰۷/۰۱ ۱۴:۳۰ وارد کنید.'
          : 'تاریخ را به شکل ۱۴۰۵/۰۷/۰۱ وارد کنید.'
      )
      return false
    }
    if (!isWithinRange(parsed, range)) {
      setTypedError(describeRange(range))
      return false
    }
    if (timePicker && !parsed.hasTime) {
      const time = selected?.hasTime ? selected : now
      emit({...parsed, hour: time.hour, minute: time.minute, hasTime: true})
    } else {
      emit(parsed)
    }
    return true
  }

  const handleCalendarChange = (date: DateObject | null) => {
    if (!date) return
    emit({
      year: date.year,
      month: date.month.number,
      day: date.day,
      hour: date.hour,
      minute: date.minute,
      hasTime: timePicker,
    })
    if (!timePicker) closeCalendar(!sheet)
  }

  const focusDayGrid = () => {
    const popover = popoverRef.current
    if (!popover) return
    const today = popover.querySelector('.rmdp-day:not(.rmdp-disabled) > [data-today]')
    const target =
      popover.querySelector<HTMLElement>('.rmdp-day.rmdp-selected') ||
      (today?.closest('.rmdp-day') as HTMLElement | null) ||
      popover.querySelector<HTMLElement>('.rmdp-day:not(.rmdp-disabled):not(.rmdp-day-hidden)')
    if (!target) return
    target.setAttribute('tabindex', '0')
    target.focus()
  }

  const place = useCallback(() => {
    const anchor = fieldRef.current
    const popover = popoverRef.current
    if (!anchor || !popover) return
    const rect = anchor.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    // The field scrolled out of sight: a calendar pinned to the edge would point at nothing.
    if (rect.bottom < 0 || rect.top > viewportHeight) {
      setOpen(false)
      return
    }
    const width = Math.min(POPOVER_WIDTH, viewportWidth - 16)
    const height = popover.offsetHeight
    const fitsBelow = rect.bottom + 6 + height <= viewportHeight - 8
    const fitsAbove = rect.top - 6 - height >= 8
    const top =
      fitsBelow || !fitsAbove
        ? Math.max(8, Math.min(rect.bottom + 6, viewportHeight - height - 8))
        : rect.top - 6 - height
    // Right-to-left: the calendar lines up with the field's right edge.
    const left = Math.max(8, Math.min(rect.right - width, viewportWidth - width - 8))
    setPosition((current) =>
      current && current.top === top && current.left === left && current.width === width
        ? current
        : {top, left, width}
    )
  }, [])

  useLayoutEffect(() => {
    if (!open) return
    if (!sheet) place()
    if (focusGridOnOpen.current) {
      focusGridOnOpen.current = false
      focusDayGrid()
    }
  }, [open, sheet, panel, calendarKey, view, place])

  useEffect(() => {
    if (!open || sheet) return undefined
    let frame = 0
    const schedule = () => {
      window.cancelAnimationFrame(frame)
      frame = window.requestAnimationFrame(place)
    }
    window.addEventListener('resize', schedule)
    window.addEventListener('scroll', schedule, true)
    const observer =
      typeof ResizeObserver === 'function' && popoverRef.current
        ? new ResizeObserver(schedule)
        : undefined
    if (observer && popoverRef.current) observer.observe(popoverRef.current)
    return () => {
      window.cancelAnimationFrame(frame)
      window.removeEventListener('resize', schedule)
      window.removeEventListener('scroll', schedule, true)
      observer?.disconnect()
    }
  }, [open, sheet, place])

  useEffect(() => {
    if (!open) return undefined
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null
      if (!target) return
      if (rootRef.current?.contains(target) || popoverRef.current?.contains(target)) return
      closeCalendar(false)
    }
    // Escape closes the calendar only, not a dialog the field sits in.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
      closeCalendar(true)
    }
    document.addEventListener('mousedown', onPointerDown, true)
    document.addEventListener('touchstart', onPointerDown, true)
    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('mousedown', onPointerDown, true)
      document.removeEventListener('touchstart', onPointerDown, true)
      document.removeEventListener('keydown', onKeyDown, true)
    }
  }, [open, closeCalendar])

  const onInputKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      // Apply the typed day before any form submit reads the old one.
      if (editing) {
        event.preventDefault()
        if (commitTyped()) closeCalendar(false)
      }
      return
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (open) {
        focusDayGrid()
      } else {
        focusGridOnOpen.current = true
        openCalendar()
      }
      return
    }
    if (event.key === 'Tab' && open) closeCalendar(false)
  }

  const onPopoverKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return
    const focusable = Array.from(
      popoverRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), [tabindex="0"]'
      ) ?? []
    )
    if (!focusable.length) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (
      (!event.shiftKey && document.activeElement === last) ||
      (event.shiftKey && document.activeElement === first)
    ) {
      event.preventDefault()
      closeCalendar(true)
    }
  }

  const canNavigate = (delta: number) => {
    if (panel === 'days') {
      const target = monthIndex(view) + delta
      return (!min || target >= monthIndex(min)) && (!max || target <= monthIndex(max))
    }
    if (panel === 'months') {
      const target = view.year + delta
      return (!min || target >= min.year) && (!max || target <= max.year)
    }
    const start = yearsStart + delta * 12
    return (!min || start + 11 >= min.year) && (!max || start <= max.year)
  }

  const navigate = (delta: number) => {
    if (!canNavigate(delta)) return
    if (panel === 'days') {
      setView(viewOfIndex(monthIndex(view) + delta))
      setCalendarKey((key) => key + 1)
    } else if (panel === 'months') {
      setView({...view, year: view.year + delta})
    } else {
      setYearsStart(yearsStart + delta * 12)
    }
  }

  const switchPanel = () => {
    if (panel === 'days') {
      setPanel('months')
    } else if (panel === 'months') {
      setYearsStart(view.year - 8)
      setPanel('years')
    } else {
      setPanel('days')
      setCalendarKey((key) => key + 1)
    }
  }

  const chooseMonth = (month: number) => {
    setView({year: view.year, month})
    setPanel('days')
    setCalendarKey((key) => key + 1)
  }

  const chooseYear = (year: number) => {
    setView(clampView({year, month: view.month}, range))
    setPanel('months')
  }

  const monthOutOfRange = (year: number, month: number) => {
    const index = monthIndex({year, month})
    return Boolean((min && index < monthIndex(min)) || (max && index > monthIndex(max)))
  }

  const yearOutOfRange = (year: number) =>
    Boolean((min && year < min.year) || (max && year > max.year))

  const mapDays = useCallback(
    ({date}: {date: DateObject}) => {
      const day = {year: date.year, month: date.month.number, day: date.day}
      const names = holidayNamesOn(day.year, day.month, day.day)
      const friday = date.weekDay.index === FRIDAY_INDEX
      const gregorianDate = toGregorian(date)
      // The library spreads these onto the day's <span>, disabled days included.
      return {
        title: [
          `${describePersianDay(day)} · ${gregorianDate.format('ddd, D MMM YYYY')}`,
          ...names,
        ].join('\n'),
        'data-off': friday || names.length > 0 ? 'true' : undefined,
        'data-holiday': names.length > 0 ? 'true' : undefined,
        'data-today': persianDayKey(day) === todayKey ? 'true' : undefined,
        children: (
          <span className='mp-dp-day'>
            <span className='mp-dp-day__jalali'>{toPersianDigits(day.day)}</span>
            <span className='mp-dp-day__gregorian'>
              {gregorianDate.day === 1
                ? `${gregorianDate.month.shortName} 1`
                : String(gregorianDate.day)}
            </span>
          </span>
        ),
      }
    },
    [todayKey]
  )

  const selectedDate = useMemo(
    () => (selected ? toPersianDateObject(selected) : undefined),
    [selected]
  )
  const minCalendarDate = useMemo(() => (min ? toPersianDateObject(min) : undefined), [min])
  const maxCalendarDate = useMemo(() => (max ? toPersianDateObject(max) : undefined), [max])

  const title =
    panel === 'days'
      ? `${PERSIAN_MONTHS[view.month - 1]} ${toPersianDigits(view.year)}`
      : panel === 'months'
      ? toPersianDigits(view.year)
      : `${toPersianDigits(yearsStart)} – ${toPersianDigits(yearsStart + 11)}`
  const subtitle =
    panel === 'days'
      ? gregorianSpanOfPersianMonth(view.year, view.month)
      : panel === 'months'
      ? gregorianSpanOfPersianYear(view.year)
      : ''
  const navigationUnit = panel === 'days' ? 'ماه' : panel === 'months' ? 'سال' : 'دوازده سال'
  const monthHolidays =
    panel === 'days'
      ? holidaysInMonth(view.year, view.month, persianMonthLength(view.year, view.month))
      : []

  const portalTarget =
    open && typeof document !== 'undefined'
      ? (rootRef.current?.closest('.modal') as HTMLElement | null) ?? document.body
      : null

  const popover =
    open && portalTarget
      ? createPortal(
          <>
            {sheet && <div className='mp-dp-scrim' aria-hidden='true' />}
            <div
              ref={popoverRef}
              id={popoverId}
              role='dialog'
              aria-modal={sheet || undefined}
              aria-label='انتخاب تاریخ'
              dir='rtl'
              className={clsx('mp-dp-popover', {'mp-dp-popover--sheet': sheet})}
              style={
                sheet
                  ? undefined
                  : position
                  ? {top: position.top, left: position.left, width: position.width}
                  : {top: 0, left: 0, visibility: 'hidden'}
              }
              onKeyDown={onPopoverKeyDown}
            >
              <div className='mp-dp-header'>
                <button
                  type='button'
                  className='mp-dp-nav'
                  aria-label={`${navigationUnit} قبل`}
                  title={`${navigationUnit} قبل`}
                  disabled={!canNavigate(-1)}
                  onClick={() => navigate(-1)}
                >
                  <ChevronIcon direction='previous' />
                </button>
                <button
                  type='button'
                  className='mp-dp-title'
                  aria-label={
                    panel === 'days'
                      ? `${title}؛ انتخاب ماه`
                      : panel === 'months'
                      ? `${title}؛ انتخاب سال`
                      : `${title}؛ بازگشت به روزها`
                  }
                  onClick={switchPanel}
                >
                  <span className='mp-dp-title__main'>
                    {title}
                    <CaretIcon open={panel !== 'days'} />
                  </span>
                  {subtitle && (
                    <span className='mp-dp-title__sub' dir='ltr'>
                      {subtitle}
                    </span>
                  )}
                </button>
                <button
                  type='button'
                  className='mp-dp-nav'
                  aria-label={`${navigationUnit} بعد`}
                  title={`${navigationUnit} بعد`}
                  disabled={!canNavigate(1)}
                  onClick={() => navigate(1)}
                >
                  <ChevronIcon direction='next' />
                </button>
              </div>

              {panel === 'days' && (
                <Calendar
                  {...CALENDAR_GRID_ROLE}
                  key={calendarKey}
                  className='mp-dp-calendar'
                  calendar={persian}
                  locale={persian_fa}
                  value={selectedDate}
                  currentDate={toPersianDateObject({year: view.year, month: view.month, day: 1})}
                  onChange={handleCalendarChange}
                  onMonthChange={(date: DateObject) =>
                    setView({year: date.year, month: date.month.number})
                  }
                  onYearChange={(date: DateObject) =>
                    setView({year: date.year, month: date.month.number})
                  }
                  minDate={minCalendarDate}
                  maxDate={maxCalendarDate}
                  mapDays={mapDays}
                  weekDays={WEEK_DAY_LABELS}
                  highlightToday={false}
                  shadow={false}
                  plugins={
                    timePicker ? [<TimePicker key='time' position='bottom' hideSeconds />] : []
                  }
                />
              )}

              {panel === 'months' && (
                <div className='mp-dp-grid' role='group' aria-label={`ماه‌های ${title}`}>
                  {PERSIAN_MONTHS.map((name, index) => {
                    const month = index + 1
                    const isSelected = selected?.year === view.year && selected?.month === month
                    const isCurrent = now.year === view.year && now.month === month
                    return (
                      <button
                        key={name}
                        type='button'
                        className={clsx('mp-dp-grid__item', {
                          'is-selected': isSelected,
                          'is-current': isCurrent,
                        })}
                        aria-pressed={isSelected}
                        disabled={monthOutOfRange(view.year, month)}
                        onClick={() => chooseMonth(month)}
                      >
                        {name}
                      </button>
                    )
                  })}
                </div>
              )}

              {panel === 'years' && (
                <div className='mp-dp-grid' role='group' aria-label='سال‌ها'>
                  {Array.from({length: 12}, (_, index) => yearsStart + index).map((year) => (
                    <button
                      key={year}
                      type='button'
                      className={clsx('mp-dp-grid__item', {
                        'is-selected': selected?.year === year,
                        'is-current': now.year === year,
                      })}
                      aria-pressed={selected?.year === year}
                      disabled={yearOutOfRange(year)}
                      onClick={() => chooseYear(year)}
                    >
                      {toPersianDigits(year)}
                    </button>
                  ))}
                </div>
              )}

              {panel === 'days' && monthHolidays.length > 0 && (
                <ul className='mp-dp-holidays' aria-label='تعطیلات رسمی این ماه'>
                  {monthHolidays.map((holiday) => (
                    <li key={holiday.day}>
                      <span className='mp-dp-holidays__day'>
                        {toPersianDigits(holiday.day)} {PERSIAN_MONTHS[view.month - 1]}
                      </span>
                      <span>{holiday.names.join(' · ')}</span>
                    </li>
                  ))}
                </ul>
              )}
              {panel === 'days' && !hasLunarHolidays(view.year) && (
                <p className='mp-dp-note'>
                  تعطیلات قمری سال {toPersianDigits(view.year)} در این تقویم نیامده است؛ فقط جمعه‌ها
                  و تعطیلات شمسی مشخص شده‌اند.
                </p>
              )}

              <div className='mp-dp-footer'>
                {selected ? (
                  <div className='mp-dp-summary'>
                    <span>
                      {describePersianDay(selected)}
                      {timePicker && selected.hasTime
                        ? ` · ساعت ${toPersianDigits(
                            `${twoDigits(selected.hour)}:${twoDigits(selected.minute)}`
                          )}`
                        : ''}
                    </span>
                    <span className='mp-dp-summary__gregorian' dir='ltr'>
                      {describeGregorianDay(selected)}
                    </span>
                  </div>
                ) : (
                  <div className='mp-dp-summary mp-dp-summary--empty'>
                    هنوز تاریخی انتخاب نشده است.
                  </div>
                )}
                <div className='mp-dp-actions'>
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary'
                    disabled={!todayAllowed}
                    title={todayAllowed ? undefined : 'امروز در بازهٔ مجاز این فیلد نیست.'}
                    onClick={pickToday}
                  >
                    امروز
                  </button>
                  {canClear && (
                    <button
                      type='button'
                      className='btn btn-sm btn-light-danger'
                      onClick={() => clear(!sheet)}
                    >
                      پاک کردن
                    </button>
                  )}
                  {(timePicker || sheet) && (
                    <button
                      type='button'
                      className='btn btn-sm btn-primary mp-dp-actions__close'
                      onClick={() => closeCalendar(!sheet)}
                    >
                      {timePicker ? 'تایید' : 'بستن'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </>,
          portalTarget
        )
      : null

  const defaultPlaceholder = openOnFieldClick
    ? timePicker
      ? 'انتخاب تاریخ و ساعت'
      : 'انتخاب تاریخ'
    : timePicker
    ? 'تاریخ و ساعت را وارد کنید'
    : 'تاریخ را وارد کنید'

  return (
    <div
      ref={rootRef}
      className={clsx(
        'mp-dp',
        {
          'mp-dp--open': open,
          'mp-dp--typed': !openOnFieldClick,
          'mp-dp--readonly': readOnly,
          'mp-dp--disabled': disabled,
        },
        className
      )}
    >
      <div ref={fieldRef} className='mp-dp__field'>
        <input
          ref={inputRef}
          id={inputId}
          type='text'
          dir='ltr'
          inputMode='numeric'
          autoComplete='off'
          spellCheck={false}
          className={clsx('form-control form-control-solid mp-dp__input', {
            'mp-dp__input--invalid': typedError,
            'mp-dp__input--clearable': canClear,
          })}
          placeholder={placeholder ?? defaultPlaceholder}
          value={text}
          readOnly={readOnly || (openOnFieldClick && coarsePointer)}
          disabled={disabled}
          aria-invalid={typedError ? true : undefined}
          aria-describedby={typedError ? errorId : undefined}
          onClick={() => {
            if (openOnFieldClick) openCalendar()
            // Typing into the field: a calendar left open would cover what is typed.
            else if (open) closeCalendar(false)
          }}
          onChange={(event) => {
            setText(
              openOnFieldClick
                ? event.target.value
                : continueTypedPersianDate(text, event.target.value, timePicker)
            )
            setEditing(true)
            setTypedError('')
          }}
          onBlur={(event) => {
            if (popoverRef.current?.contains(event.relatedTarget as Node | null)) return
            commitTyped()
          }}
          onKeyDown={onInputKeyDown}
        />
        <div className='mp-dp__actions'>
          {canClear && (
            <button
              type='button'
              className='mp-dp__action'
              aria-label='پاک کردن تاریخ'
              title='پاک کردن'
              onClick={() => clear(true)}
            >
              <ClearIcon />
            </button>
          )}
          <button
            type='button'
            className='mp-dp__action mp-dp__action--calendar'
            aria-label='انتخاب از تقویم'
            title='تقویم'
            aria-haspopup='dialog'
            aria-expanded={open}
            aria-controls={open ? popoverId : undefined}
            disabled={locked}
            onClick={() => (open ? closeCalendar(true) : openCalendar())}
          >
            <CalendarIcon />
          </button>
        </div>
      </div>
      {typedError && (
        <div id={errorId} className='mp-dp__error' role='alert'>
          {typedError}
        </div>
      )}
      {popover}
    </div>
  )
}
